import React, { forwardRef } from 'react'

import { cn } from '@/lib/utils'
import Icon, { IconTypes } from '@/components/Icon'
import LcIcon from '@/components/livecontact/icons'

type Props = {
  /** Legacy glyph name (social brands, industry links, file types). */
  icon?: IconTypes
  /** System glyph rendered instead of `icon` when given. */
  glyph?: React.ReactNode
  title: string
  subtitle?: string
  url?: string
  /** Glyph colour override; used where a link carries its own brand colour. */
  bgColor?: string
  isExternal?: boolean
  /** The card's hero action: brand-filled, glass label, glow. One per card. */
  primary?: boolean
}

/**
 * Tappable action row for the public card: leading glyph, label (+ optional detail line),
 * trailing arrow. 48px tall on its own, 56px when it carries a detail line — both well over
 * the 44px tap minimum. Press is a 0.985 scale so a stack of rows settles together.
 */
const ActionItem = forwardRef<HTMLAnchorElement | HTMLButtonElement, Props>(
  ({ icon, glyph, title, subtitle, url, bgColor, isExternal, primary }, ref) => {
    if (!url) return null

    const className = cn(
      'group/row relative flex min-h-[48px] w-full items-center gap-2 overflow-hidden rounded-[12px] border px-5 py-2.5 text-left outline-none',
      'transition-[background-color,border-color,transform,filter] duration-[160ms] ease-out-strong',
      'focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface active:scale-[0.985]',
      primary
        ? // The system primary button's recipe (`lc-action-primary`), in the card's colour.
          'lc-action-primary hover:brightness-[1.06] before:pointer-events-none before:absolute before:left-1/2 before:top-full before:h-[19px] before:w-[228px] before:-translate-x-1/2 before:-translate-y-[9px] before:rounded-[50%] before:bg-white/10 before:blur-[12px]'
        : 'border-line bg-surface hover:bg-canvas/70 dark:hover:bg-surface-2'
    )

    const content = (
      <>
        <span
          aria-hidden
          style={!primary && bgColor ? { color: bgColor } : undefined}
          className={cn(
            'inline-flex shrink-0 items-center justify-center [&>svg]:size-4',
            primary ? 'text-[color:var(--brand-on,#fff)]' : 'text-ink-2'
          )}
        >
          {glyph ?? (icon ? <Icon name={icon} className="size-4 fill-current" /> : null)}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block truncate text-[14px] font-medium leading-[1.25] tracking-[-0.2px]',
              primary ? 'lc-text-glass' : 'text-ink'
            )}
          >
            {title}
          </span>
          {subtitle && (
            <span
              title={subtitle}
              className={cn(
                'mt-0.5 block truncate text-[12px] leading-[1.3] tracking-[-0.2px]',
                primary ? 'text-[color:var(--brand-on,#fff)] opacity-75' : 'text-ink-4'
              )}
            >
              {subtitle}
            </span>
          )}
        </span>
        <LcIcon
          name="arrow-right"
          size={14}
          className={cn(
            'shrink-0 transition-transform duration-[160ms] ease-out-strong group-hover/row:translate-x-0.5',
            primary ? 'text-[color:var(--brand-on,#fff)] opacity-85' : 'text-ink-4'
          )}
        />
      </>
    )

    if (isExternal) {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {content}
        </a>
      )
    }

    return (
      <button ref={ref as React.Ref<HTMLButtonElement>} type="button" className={className}>
        {content}
      </button>
    )
  }
)

ActionItem.displayName = 'ActionItem'

export default ActionItem
